/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useFormatMessage } from 'app/common/hooks';

import messages from './SliceHTMLWidget.messages';
import { styleHtml } from 'app/common/utils/HtmlUtils';
import { useOverflowDetector } from './useOverflowDetector';

/**
 * Modified version of HTMLWidget that allows displaying the text at a truncated value that can be expanded to show the entire text.
 * Defaults slicing the text at a height of h-20 or 80px.
 */
const SliceHTMLWidget = ({
  className,
  content,
  readMoreClassName = '',
  cutoffHeight = 'h-20',
  readMoreTextClass = 'text-gray-500 hover:text-gray-700'
}) => {
  const formatMessage = useFormatMessage();
  const [readingMore, setReadingMore] = React.useState(false);
  const toggleReadingMore = React.useCallback(
    () => setReadingMore(prev => !prev),
    []
  );
  const autoExpandedRef = React.useRef(false);

  const { ref: sectionRef, hasOverflow } = useOverflowDetector({
    collapsed: !readingMore,
    deps: [content, cutoffHeight]
  });

  // Expand text on large screens by default
  React.useEffect(() => {
    if (hasOverflow && !readingMore && !autoExpandedRef.current) {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setReadingMore(true);
        autoExpandedRef.current = true;
      }
    }
  }, [hasOverflow, readingMore]);

  if (!content) {
    return null;
  }

  const args = {
    html: content,
    options: {
      elementClasses: {
        ul: {
          topLevelClasses: ['pl-4']
        },
        ol: {
          topLevelClasses: ['pl-4']
        }
      }
    }
  };

  return (
    <div className={'flex flex-col'}>
      <section
        ref={sectionRef}
        className={classNames(
          'block pl-4 html-field overflow-hidden',
          className,
          {
            [cutoffHeight]: !readingMore
          }
        )}
        dangerouslySetInnerHTML={{
          __html: styleHtml(args)
        }}
      />
      {(hasOverflow || readingMore) && (
        <button
          onClick={toggleReadingMore}
          className={classNames(
            'flex justify-start focus:outline-none',
            readMoreClassName,
            readMoreTextClass
          )}
        >
          {readingMore
            ? formatMessage(messages.showLess)
            : formatMessage(messages.readMore)}
        </button>
      )}
    </div>
  );
};

SliceHTMLWidget.propTypes = {
  className: PropTypes.string,
  content: PropTypes.string
};

export default SliceHTMLWidget;
export { SliceHTMLWidget };
